import axios from 'axios'
import { create } from 'zustand'

import { isQueuedAnswer } from '../lib/practiceSession'
import * as lessonsApi from '../services/lessons'

/**
 * Catalogue and current-exercise state.
 *
 * `result` holds the `AttemptResultDto` from the last submission
 * (`{attemptId, assessment, xpAwarded, ...}`) for the active lesson. There is
 * no server-side "session" concept in the real API — submission is one
 * request, not open/analyze/close — so unlike an in-progress draft, nothing
 * here is abandoned by navigating away.
 *
 * Since plan B (V9-b/V9-w) an answer to `attempts_submit` is no longer the
 * same thing as a score: it can also be a 202 `AttemptQueuedDto` — the take
 * is stored, `status: 'queued'`, nothing assessed and no XP awarded. That
 * answer lands in `queued`, never in `result`, because everything
 * downstream of `result` reads it as a score: `ScoreSummary` draws the
 * bands and «+N XP», `WordFeedback` the words, `ShareToFeed` offers to
 * publish an attempt the feed will not take until it is scored. A queue
 * receipt in that slot would show a zero and a share button for a take
 * nobody has judged yet.
 *
 * Neither answer raises the learner's XP or streak here. They are the
 * server's numbers (`me/profile`, `me/stats`), re-read on the next
 * dashboard/profile load; a queued take has nothing to raise them by at all
 * — its XP is awarded when the requeue job or an admin scores it, and the
 * usual `result` notification is what tells the learner.
 */
export const useLearningStore = create((set) => ({
  lessons: [],
  loading: false,
  error: null,

  lesson: null,
  result: null,
  /** The `AttemptQueuedDto` of a take plan B accepted but has not scored (V9-w). */
  queued: null,
  analyzing: false,

  async loadLessons({ level } = {}) {
    set({ loading: true, error: null })
    try {
      const data = await lessonsApi.listLessons({ level })
      set({ lessons: data.items, loading: false })
    } catch (error) {
      set({ loading: false, error })
    }
  },

  async loadLesson(lessonId) {
    set({ loading: true, error: null, lesson: null, result: null, queued: null })
    try {
      const lesson = await lessonsApi.getLesson(lessonId)
      set({ lesson, loading: false })
      return lesson
    } catch (error) {
      set({ loading: false, error })
      throw error
    }
  },

  async submitRecording(lessonId, sentenceIndex, blob, filename, options = {}) {
    set({ analyzing: true, error: null })
    try {
      const answer = await lessonsApi.submitAttempt(lessonId, sentenceIndex, blob, filename, options)
      if (isQueuedAnswer(answer)) set({ queued: answer, result: null, analyzing: false })
      else set({ result: answer, queued: null, analyzing: false })
      return answer
    } catch (error) {
      // A cancelled submit is the learner's own choice, not a failure to report.
      set({ analyzing: false, error: axios.isCancel(error) ? null : error })
      throw error
    }
  },

  reset() {
    set({ lesson: null, result: null, queued: null, analyzing: false, error: null })
  },
}))
